import { useEnabledFeatures } from './enabled-features/use-enabled-features';
import { useToast } from '@cad-arc';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '../../current-user/current-user';
import { useCadNextAdditionalInfoNotifications } from '../../event-notifications/use-additional-info-notifications';
import { FR_EVENT_DETAILS_SUB_PATH } from '../../router/constants';
import { AdditionalInfosProviderUtils } from '../types';
import { AdditionalInfoType } from '../../additional-infos/types';
import { useApplicationType } from './use-application-type';

export function useAdditionalInfosProviderUtils(): AdditionalInfosProviderUtils {
    const { userId } = useCurrentUser();
    const notificationManager = useCadNextAdditionalInfoNotifications();
    const toast = useToast();
    const { isFirstResponder } = useApplicationType();
    const {
        filterOutSystemMessagesFromNarrativeByDefault: noSystemMessagesByDefault,
        proQaEnhancementsEnabled,
    } = useEnabledFeatures();
    const location = useLocation();
    const isFrEventDetailsPage = location.pathname.includes(FR_EVENT_DETAILS_SUB_PATH);

    const showErrorAlert = useCallback(
        ({ message: description }) => {
            toast({
                description,
                status: 'error',
            });
        },
        [toast]
    );

    const initialFilters = useMemo(() => {
        const initialFilters = [AdditionalInfoType.USER];

        if (isFirstResponder || proQaEnhancementsEnabled) {
            initialFilters.push(AdditionalInfoType.PROQA);
        }

        if (proQaEnhancementsEnabled) {
            initialFilters.push(
                ...[
                    AdditionalInfoType.PRO_QA_DESC_INFO,
                    AdditionalInfoType.PRO_QA_COMMENT,
                    AdditionalInfoType.PRO_QA_SCRIPT,
                    AdditionalInfoType.PRO_QA_KEY_QUESTION,
                    AdditionalInfoType.PRO_QA_CRITICAL,
                    AdditionalInfoType.PRO_QA_UPDATE,
                    AdditionalInfoType.PRO_QA_URGENT,
                ]
            );
        }

        if (!noSystemMessagesByDefault || (isFirstResponder && isFrEventDetailsPage)) {
            initialFilters.push(AdditionalInfoType.SYSTEM);
        }

        return initialFilters;
    }, [
        isFirstResponder,
        noSystemMessagesByDefault,
        isFrEventDetailsPage,
        proQaEnhancementsEnabled,
    ]);

    return {
        showErrorAlert,
        userId,
        notificationManager,
        initialFilters,
    };
}
